import { Button, Card } from "flowbite-react";
import { FaCheckCircle } from "react-icons/fa";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import SellData from "./SellData";
import VehicleData from "./VehicleData";
import SellerData from "./SellerData";
import BuyerData from "./BuyerData";
import ReviewData from "./ReviewData";
import axios from "axios";
import { generateCommunicateSellPdf } from "../../utils/pdf-cv.exporter";
import useCEP from "../../hooks/useCEP";
import { CVS } from "../../config/apiConfig";
import axiosInstance from "../../config/axiosInstance";

const ComunicateSell = ({ title, message }) => {
  const location = useLocation();
  const [communicateSell, setCommunicateSell] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  const [states, setStates] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const prevStepRef = useRef(activeStep);

  useEffect(() => {
    const receivedCv = location.state?.cv;

    if (receivedCv) {
      setCommunicateSell(receivedCv);
    }
  }, [location.state]);

  useEffect(() => {
    async function fetchStates() {
      try {
        const response = await axios.get(
          "https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome"
        );
        setStates(
          response?.data?.map((st) => ({
            value: st?.sigla,
            label: st?.nome,
          }))
        );
      } catch (error) {
        console.error("Erro ao buscar estados:", error);
      }
    }
    fetchStates();
  }, []);

  useEffect(() => {
    const autoSave = async () => {
      if (!communicateSell) return;

      try {
        setIsSubmitting(true);
        const response = await axiosInstance.put(CVS.UPDATE, communicateSell);
        setCommunicateSell(response.data);
      } catch (err) {
        console.error("Erro ao salvar dados do passo:", err);
      } finally {
        setIsSubmitting(false);
      }
    };

    // Só salva se o novo passo for maior (usuário foi para frente)
    if (activeStep > prevStepRef.current) {
      autoSave();
    }

    prevStepRef.current = activeStep; // atualiza o passo anterior
  }, [activeStep]);

  return (
    <Card className="mb-3 flex-1">
      <ol className="flex items-center w-full text-sm font-medium text-center text-gray-500 dark:text-gray-400 sm:text-base">
        <li className="flex md:w-full items-center text-primary dark:text-blue-500 sm:after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10 dark:after:border-gray-700">
          <span className="flex items-center after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200 dark:after:text-gray-500">
            {activeStep >= 1 ? (
              <FaCheckCircle color="green" className="mr-2" />
            ) : (
              <span className="me-2">1</span>
            )}
            <span className="hidden md:block">Veículo</span>
          </span>
        </li>
        <li className="flex md:w-full items-center after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10 dark:after:border-gray-700">
          <span className="flex items-center after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200 dark:after:text-gray-500">
            {activeStep >= 2 ? (
              <FaCheckCircle color="green" className="mr-2" />
            ) : (
              <span className="me-2">2</span>
            )}
            <span className="hidden md:block">Venda</span>
          </span>
        </li>
        <li className="flex md:w-full items-center after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10 dark:after:border-gray-700">
          <span className="flex items-center after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200 dark:after:text-gray-500">
            {activeStep >= 3 ? (
              <FaCheckCircle color="green" className="mr-2" />
            ) : (
              <span className="me-2">3</span>
            )}
            <span className="hidden md:block whitespace-nowrap">Vendedor</span>
          </span>
        </li>
        <li className="flex md:w-full items-center after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10 dark:after:border-gray-700">
          <span className="flex items-center after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200 dark:after:text-gray-500">
            {activeStep >= 4 ? (
              <FaCheckCircle color="green" className="mr-2" />
            ) : (
              <span className="me-2">4</span>
            )}
            <span className="hidden md:block whitespace-nowrap">Comprador</span>
          </span>
        </li>
        <li className="flex items-center">
          {activeStep === 5 ? (
            <FaCheckCircle color="green" className="mr-2" />
          ) : (
            <span className="me-2">5</span>
          )}
          <span className="hidden md:block">Revisão</span>
        </li>
      </ol>

      {activeStep === 1 && (
        <VehicleData
          setActiveStep={setActiveStep}
          communicateSell={communicateSell}
          setCommunicateSell={setCommunicateSell}
          states={states}
          isSubmitting={isSubmitting}
        />
      )}

      {activeStep === 2 && (
        <SellData
          communicateSell={communicateSell}
          setCommunicateSell={setCommunicateSell}
          setActiveStep={setActiveStep}
          states={states}
          isSubmitting={isSubmitting}
        />
      )}

      {activeStep === 3 && (
        <SellerData
          setActiveStep={setActiveStep}
          communicateSell={communicateSell}
          setCommunicateSell={setCommunicateSell}
          states={states}
          isSubmitting={isSubmitting}
        />
      )}

      {activeStep === 4 && (
        <BuyerData
          setActiveStep={setActiveStep}
          communicateSell={communicateSell}
          setCommunicateSell={setCommunicateSell}
          states={states}
          isSubmitting={isSubmitting}
        />
      )}

      {activeStep === 5 && (
        <ReviewData
          setActiveStep={setActiveStep}
          communicateSell={communicateSell}
          setCommunicateSell={setCommunicateSell}
        />
      )}
    </Card>
  );
};

export default ComunicateSell;
